import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./Header.css";

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const currentLanguage = i18n.language;

  const getAvailableLanguages = () => {
    const languages = [
      { code: "pt-BR", name: "Português" },
      { code: "en-US", name: "English" },
      { code: "es-ES", name: "Español" },
    ];

    return languages.filter((lang) => lang.code !== currentLanguage);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigationItems = [
    { key: "about", href: "#about" },
    { key: "people", href: "#people" },
    { key: "research", href: "#research" },
    { key: "meetings", href: "#meetings" },
    { key: "contact", href: "#contact" },
  ];

  const handleSmoothScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const headerHeight = 80;
      const elementPosition = targetElement.offsetTop;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo */}
        <div className="header-logo">
          <img src="/long-horizontal.png" alt={t("header.logoAlt")} />
        </div>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          <ul className="nav-list">
            {navigationItems.map((item) => (
              <li key={item.key}>
                <a
                  href={item.href}
                  className="nav-link"
                  onClick={(e) => handleSmoothScroll(e, item.href)}
                >
                  {t(`header.nav.${item.key}`)}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Language Toggle */}
        <div className="header-language">
          <div className="language-toggle-horizontal">
            {getAvailableLanguages().map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className="language-option-inline"
              >
                {lang.name}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label={t("header.mobileMenuToggle")}
        >
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
          ></span>
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
          ></span>
          <span
            className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
          ></span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${isMobileMenuOpen ? "open" : ""}`}>
        <ul className="mobile-nav-list">
          {navigationItems.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                className="mobile-nav-link"
                onClick={(e) => {
                  handleSmoothScroll(e, item.href);
                  setIsMobileMenuOpen(false);
                }}
              >
                {t(`header.nav.${item.key}`)}
              </a>
            </li>
          ))}
          <li className="mobile-language-section">
            <div className="mobile-language-buttons">
              {getAvailableLanguages().map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className="mobile-language-option"
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;
